import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { generateReportPdf } from '@/lib/pdfReport';
import { getExpertBenchmark } from '@/config/tests';
import type { RoleType } from '@/config/tests';
import {
  calculateCulturalFit,
  computeCultureScores,
  computeCultureScoresFromResponses,
  extractCultureActualAnswersFromAssessmentDoc,
  type CultureResponse,
} from '@/lib/cultureEngine';

/**
 * Dispara la generación del PDF en el cliente y registra el log en Firestore.
 * No almacenamos el PDF en servidor; solo metadatos en reportLogs.
 */
export function ResultPdfPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const { appUser, canAccessCompany } = useAuth();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!attemptId || !appUser) return;
    const run = async () => {
      try {
        const [attemptSnap, resultSnap] = await Promise.all([
          getDoc(doc(db, 'attempts', attemptId)),
          getDoc(doc(db, 'results', attemptId)),
        ]);
        if (!attemptSnap.exists() || !resultSnap.exists()) {
          setStatus('error');
          setError('Resultado no encontrado');
          return;
        }
        const att = attemptSnap.data();
        const res = resultSnap.data();
        if (!canAccessCompany(att.companyId)) {
          setStatus('error');
          setError('Sin acceso');
          return;
        }
        const [companySnap, processSnap, candidateSnap] = await Promise.all([
          getDoc(doc(db, 'companies', att.companyId)),
          att.processId ? getDoc(doc(db, 'processes', att.processId)) : null,
          att.candidateId ? getDoc(doc(db, 'candidates', att.candidateId)) : null,
        ]);
        const company = companySnap.exists() ? companySnap.data() : {};
        const processData = processSnap?.exists() ? processSnap.data() : {};
        const processTitle = (processData?.title as string) ?? '';
        const candidate = candidateSnap?.exists() ? candidateSnap.data() ?? {} : {};
        const roleType = processData?.roleType as string | undefined;
        const jobProfile =
          (processData?.jobProfile as Record<string, unknown> | undefined) ??
          (roleType ? getExpertBenchmark(roleType as RoleType) : null);

        let culturalFit: {
          percent: number;
          candidateScores: ReturnType<typeof computeCultureScores>;
          companyScores: ReturnType<typeof computeCultureScores>;
        } | null = null;

        try {
          const responsesSnap = await getDoc(doc(db, 'responses', attemptId));
          if (responsesSnap.exists()) {
            const answers = (responsesSnap.data()?.answers as Record<string, unknown> | undefined) ?? {};
            const candidateCultureAnswers = answers.CULTURE_FIT as Record<string, number> | undefined;
            if (candidateCultureAnswers && Object.keys(candidateCultureAnswers).length > 0) {
              const candidateScores = computeCultureScores(candidateCultureAnswers);
              const cultureSnap = await getDocs(
                query(collection(db, 'culture_assessments'), where('companyId', '==', att.companyId))
              );
              const cultureResponses: CultureResponse[] = [];
              cultureSnap.forEach((row) => {
                const data = row.data() as Record<string, unknown>;
                if (data.status !== 'completed') return;
                const actual = extractCultureActualAnswersFromAssessmentDoc(data);
                if (!actual) return;
                cultureResponses.push({
                  department: typeof data.department === 'string' ? data.department : undefined,
                  answers: actual,
                });
              });
              const companyScores = computeCultureScoresFromResponses(cultureResponses);
              culturalFit = {
                percent: calculateCulturalFit(candidateScores, companyScores),
                candidateScores,
                companyScores,
              };
            }
          }
        } catch (cultureErr) {
          if (import.meta.env?.DEV) console.warn('[ResultPdfPage] No se pudo calcular ajuste cultural:', cultureErr);
        }

        await generateReportPdf({
          attemptId,
          candidateName: (candidate.fullName as string) || (candidate.email as string) || 'Candidato',
          processTitle,
          companyName: company.name || 'Empresa',
          logoUrl: company.branding?.logoUrl,
          primaryColor: company.branding?.primaryColor,
          scores: res.scores ?? [],
          moduleAggregates: res.moduleAggregates ?? {},
          computedAt: res.computedAt as string | undefined,
          jobProfile: jobProfile ?? undefined,
          roleType: roleType ?? null,
          culturalFit,
        });

        await addDoc(collection(db, 'reportLogs'), {
          companyId: att.companyId,
          processId: att.processId,
          candidateId: att.candidateId ?? '',
          attemptId,
          generatedByUid: appUser.uid,
          generatedAt: new Date().toISOString(),
          reportVersion: 1,
        });
        setStatus('ready');
      } catch (e) {
        setStatus('error');
        setError(e instanceof Error ? e.message : 'Error al generar PDF');
      }
    };
    run();
  }, [attemptId, appUser, canAccessCompany]);

  if (status === 'loading') return <p className="text-muted-foreground">Generando PDF...</p>;
  if (status === 'error') {
    return (
      <div className="space-y-4">
        <p className="text-destructive">{error}</p>
        <Button asChild>
          <Link to={`/app/results/${attemptId}`}>Volver al resultado</Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">PDF generado y descargado. El registro quedó guardado.</p>
      <Button asChild>
        <Link to={`/app/results/${attemptId}`}>Volver al resultado</Link>
      </Button>
    </div>
  );
}
